import {useNavigate, useOutletContext} from "react-router-dom";
import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import {useTiersPayant} from "../../context/TiersPayantContext";
import {Controller, useForm} from "react-hook-form";
import {Button, Form, Modal, Row} from "react-bootstrap";
import Select from 'react-select'
import {useHealthInsurances} from "../../hooks/api/healthInsurances";
import {useCustomers} from "../../hooks/api/customer";
import DatePicker from "react-date-picker";

const CreateTiersPayant = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [canClose, setCanClose] = useState(false)
    const [formErrors, setFormErrors] = useState(false)
    const [healthInsurancesOptions, setHealthInsurancesOptions] = useState([])
    const [customersOptions, setCustomersOptions] = useState([])
    const {setSuccessMessage} = useOutletContext()
    const {loading, apiErrors, add, load, resetApiErrors} = useTiersPayant()
    const [{healthInsurances}, {fetchAll: fetchAllHealthInsurance}] = useHealthInsurances()
    const [{customers}, {fetchAll: fetchAllCustomer}] = useCustomers()

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
    } = useForm({})

    useEffect(() => {
        if (healthInsurances) {
            const options = healthInsurances.map((healthInsurance => {
                return { label: healthInsurance.name, value: healthInsurance.id }
            }))
            setHealthInsurancesOptions(options)
        }

    }, [healthInsurances])

    useEffect(() => {
        if (customers) {
            const options = customers.map((customer => {
                return { label: customer.name, value: customer.id }
            }))
            setCustomersOptions(options)
        }

    }, [customers])

    useEffect(() => {
        setSuccessMessage(null)
        fetchAllHealthInsurance()
        fetchAllCustomer()
    }, [fetchAllCustomer, fetchAllHealthInsurance, setSuccessMessage]);

    useEffect(() => {
        setFormErrors({...errors, ...apiErrors})
    }, [errors, apiErrors])

    useEffect(() => {
        setFormErrors({...apiErrors, ...errors})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiErrors]);

    useEffect(() => {
        if (canClose && !loading && !apiErrors) {
            setSuccessMessage(t('tiers-payant.add.success_message'))
            handleClose()
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canClose, loading, apiErrors]);

    const onSubmit = async (inputs) => {
        load()
        await add(inputs)
        setCanClose(true)
    }

    const handleClose = () => {
        resetApiErrors()
        navigate('/tiers-payant')
    }

    return (
        <Modal
            show={true}
            onHide={handleClose}
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t('tiers-payant.add.title')}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body>
                    <Row>
                        <Form.Group className="mb-3 col-md-6" controlId="customer.healthInsuranceId">
                            <Form.Label>{t('tiers-payant.form.healthInsurance.label')}</Form.Label>
                            <Controller
                                name="healthInsuranceId"
                                control={control}
                                render={({ field, value, name, ref }) => (
                                    <Select
                                        name={field.name}
                                        placeholder={t('tiers-payant.form.healthInsurance.place-holder')}
                                        inputRef={field.ref}
                                        options={healthInsurancesOptions}
                                        value={healthInsurancesOptions.find((c) => c.value === field.value)}
                                        onChange={val => field.onChange(val.value)}
                                        isInvalid={formErrors && formErrors.healthInsuranceId}
                                        className={`p-0 form-control form-control-lg ${formErrors && formErrors.healthInsuranceId ? ' is-invalid' : ''}`}
                                    />)
                                    }
                                size="lg"
                                {...register("healthInsuranceId", {
                                    required: 'error-empty',
                                })}

                            />
                            {formErrors && formErrors.healthInsuranceId && (
                                <Form.Control.Feedback type="invalid">
                                    {t(`tiers-payant.form.healthInsurance.errors.${formErrors.healthInsuranceId.message}`)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3 col-md-6" controlId="customer.customerId">
                            <Form.Label>{t('tiers-payant.form.customer.label')}</Form.Label>
                            <Controller
                                name="customerId"
                                control={control}
                                render={({ field, value, name, ref }) => (
                                    <Select
                                        name={field.name}
                                        placeholder={t('tiers-payant.form.customer.place-holder')}
                                        inputRef={field.ref}
                                        options={customersOptions}
                                        value={customersOptions.find((c) => c.value === field.value)}
                                        onChange={val => field.onChange(val.value)}
                                        isInvalid={formErrors && formErrors.customerId}
                                        className={`p-0 form-control form-control-lg ${formErrors && formErrors.customerId ? ' is-invalid' : ''}`}
                                    />)
                                }
                                size="lg"
                                {...register("customerId", {
                                    required: 'error-empty',
                                })}

                            />
                            {formErrors && formErrors.customerId && (
                                <Form.Control.Feedback type="invalid">
                                    {t(`tiers-payant.form.customer.errors.${formErrors.customerId.message}`)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group className="mb-3 col-md-6" controlId="tiers-payant.patientName">
                            <Form.Label>{t('tiers-payant.form.patientName.label')}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={t('tiers-payant.form.patientName.place-holder')}
                                size="lg"
                                {...register("patientName", {
                                    required: 'error-empty',
                                })}
                                disabled={loading}
                                isInvalid={formErrors && formErrors.patientName}
                            />
                            {formErrors && formErrors.patientName && (
                                <Form.Control.Feedback type="invalid">
                                    {t(`tiers-payant.form.patientName.errors.${formErrors.patientName.message}`)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3 col-md-6" controlId="tiers-payant.nirCode">
                            <Form.Label>{t('tiers-payant.form.nirCode.label')}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={t('tiers-payant.form.nirCode.place-holder')}
                                size="lg"
                                {...register("nirCode", {
                                    required: 'error-empty',
                                })}
                                disabled={loading}
                                isInvalid={formErrors && formErrors.nirCode}
                            />
                            {formErrors && formErrors.nirCode && (
                                <Form.Control.Feedback type="invalid">
                                    {t(`tiers-payant.form.nirCode.errors.${formErrors.nirCode.message}`)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group className="mb-3 col-md-6" controlId="tiers-payant.invoiceDate">
                            <Form.Label>{t('tiers-payant.form.invoiceDate.label')}</Form.Label>
                            <Controller
                                name="text"
                                control={control}
                                render={({ field, value, name, ref }) => (
                                    <DatePicker
                                        onChange={field.onChange}
                                        value={field.value}
                                        className={`p-0 form-control form-control-lg ${formErrors && formErrors.invoiceDate ? ' is-invalid' : ''}`}
                                        format={"dd-MM-y"}
                                    />
                                    )
                                }
                                size="lg"
                                {...register("invoiceDate", {
                                    required: 'error-empty',
                                })}
                            />
                            {formErrors && formErrors.invoiceDate && (
                                <Form.Control.Feedback type="invalid">
                                    {t(`tiers-payant.form.invoiceDate.errors.${formErrors.invoiceDate.message}`)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3 col-md-6" controlId="tiers-payant.invoiceNumber">
                            <Form.Label>{t('tiers-payant.form.invoiceNumber.label')}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={t('tiers-payant.form.invoiceNumber.place-holder')}
                                size="lg"
                                {...register("invoiceNumber", {
                                    required: 'error-empty',
                                })}
                                disabled={loading}
                                isInvalid={formErrors && formErrors.invoiceNumber}
                            />
                            {formErrors && formErrors.invoiceNumber && (
                                <Form.Control.Feedback type="invalid">
                                    {t(`tiers-payant.form.invoiceNumber.errors.${formErrors.invoiceNumber.message}`)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group className="mb-3 col-md-6" controlId="tiers-payant.fseCode">
                            <Form.Label>{t('tiers-payant.form.fseCode.label')}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={t('tiers-payant.form.fseCode.place-holder')}
                                size="lg"
                                {...register("fseCode", {
                                    required: 'error-empty',
                                })}
                                disabled={loading}
                                isInvalid={formErrors && formErrors.fseCode}
                            />
                            {formErrors && formErrors.fseCode && (
                                <Form.Control.Feedback type="invalid">
                                    {t(`tiers-payant.form.fseCode.errors.${formErrors.fseCode.message}`)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3 col-md-6" controlId="tiers-payant.remainingAmount">
                            <Form.Label>{t('tiers-payant.form.remainingAmount.label')}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={t('tiers-payant.form.remainingAmount.place-holder')}
                                size="lg"
                                {...register("remainingAmount", {
                                    required: 'error-empty',
                                })}
                                disabled={loading}
                                isInvalid={formErrors && formErrors.remainingAmount}
                            />
                            {formErrors && formErrors.remainingAmount && (
                                <Form.Control.Feedback type="invalid">
                                    {t(`tiers-payant.form.remainingAmount.errors.${formErrors.remainingAmount.message}`)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                    >
                        {t('tiers-payant.add.button.cancel')}
                    </Button>
                    <Button variant="primary" type="submit">
                        {t('tiers-payant.add.button.save')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

export default CreateTiersPayant